import apiClient from "./apiClient";

/**
 * Login User / Admin with backend middleware
 * @param {Object} credentials - { email, password }
 * @returns {Promise<Object>} - { token, user, message }
 */
export const loginUser = async ({ email, password }) => {
  try {
    const data = await apiClient.post("/api/auth/login", {
      email: email.trim().toLowerCase(),
      password,
    });

    if (!data.success) {
      const errorMsg = data.message || "Failed to sign in. Please verify your credentials.";
      throw new Error(errorMsg);
    }

    return data;
  } catch (error) {
    throw new Error(error.message || "Failed to sign in. Please verify your credentials.");
  }
};

/**
 * Login Employee
 * @param {Object} credentials - { companyCode, mobile, countryCode, isWeb }
 * @returns {Promise<Object>} - { token, user, employee, require2FA, require2FASetup, qrCode, secret, ... }
 */
export const loginEmployee = async ({ companyCode, mobile, countryCode = "+91", isWeb = true }) => {
  try {
    const data = await apiClient.post("/api/employees/login", {
      companyCode: companyCode.trim(),
      mobile: mobile.trim(),
      countryCode,
      isWeb,
    });

    if (!data.success) {
      const errorMsg = data.message || "Employee not found with this number & company code.";
      throw new Error(errorMsg);
    }

    return data;
  } catch (error) {
    throw new Error(error.message || "Employee not found with this number & company code.");
  }
};

/**
 * Verify Employee 2FA Setup (First-time Authenticator setup)
 * @param {Object} payload - { employeeId, companyCode, token }
 * @returns {Promise<Object>} - { token, employee, message, ... }
 */
export const verifyEmployee2FASetup = async ({ employeeId, companyCode, token }) => {
  try {
    const data = await apiClient.post("/api/employees/two-factor-auth/setup-verifications", {
      employeeId,
      companyCode: (companyCode || "").trim(),
      token: String(token).trim(),
    });

    if (!data.success) {
      throw new Error(data.message || "Failed to verify 2FA setup code.");
    }

    return data;
  } catch (error) {
    throw new Error(error.message || "Failed to verify 2FA setup code.");
  }
};

/**
 * Verify Employee 2FA Code (Daily / Session Login)
 * @param {Object} payload - { employeeId, companyCode, token }
 * @returns {Promise<Object>} - { token, employee, message, ... }
 */
export const verifyEmployee2FACode = async ({ employeeId, companyCode, token }) => {
  try {
    const data = await apiClient.post("/api/employees/two-factor-auth/verifications", {
      employeeId,
      companyCode: (companyCode || "").trim(),
      token: String(token).trim(),
    });

    if (!data.success) {
      throw new Error(data.message || "Failed to verify 2FA code.");
    }

    return data;
  } catch (error) {
    throw new Error(error.message || "Failed to verify 2FA code.");
  }
};

/**
 * Verify Admin 2FA Code (Daily / Session Login)
 * @param {Object} payload - { userId, companyCode, token }
 * @returns {Promise<Object>} - { token, user, message, ... }
 */
export const verifyAdmin2FACode = async ({ userId, companyCode, token }) => {
  try {
    const data = await apiClient.post("/api/auth/verify-2fa", {
      userId,
      companyCode: (companyCode || "").trim(),
      token: String(token).trim(),
    });

    if (!data.success) {
      throw new Error(data.message || "Failed to verify 2FA code.");
    }

    return data;
  } catch (error) {
    throw new Error(error.message || "Failed to verify 2FA code.");
  }
};

/**
 * Register User / Company Trial
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
export const registerUser = async (payload) => {
  try {
    const data = await apiClient.post("/api/auth/register", payload);
    if (!data.success) {
      throw new Error(data.message || "Failed to register.");
    }
    return data;
  } catch (error) {
    throw new Error(error.message || "Failed to register.");
  }
};

/**
 * Request Password Reset Email
 * @param {string} email
 * @returns {Promise<Object>}
 */
export const requestPasswordReset = async (email) => {
  try {
    const data = await apiClient.post("/api/auth/password-reset-requests", {
      email: email.trim().toLowerCase(),
    });
    if (!data.success) {
      throw new Error(data.message || "Failed to send password reset email.");
    }
    return data;
  } catch (error) {
    throw new Error(error.message || "Failed to send password reset email.");
  }
};

/**
 * Reset Password with Token
 * @param {Object} payload - { token, newPassword }
 * @returns {Promise<Object>}
 */
export const resetPasswordWithToken = async ({ token, newPassword }) => {
  try {
    const data = await apiClient.post("/api/auth/password-resets", {
      token,
      newPassword,
    });
    if (!data.success) {
      throw new Error(data.message || "Failed to reset password.");
    }
    return data;
  } catch (error) {
    throw new Error(error.message || "Failed to reset password.");
  }
};



